import React, { useMemo } from 'react';
import styled from 'styled-components';
import { BreakpointSize, breakpointsWidthHelper } from '../../fundamentals/breakpoints/breakpoints';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { IGutterProps, useGutterCss } from '../../fundamentals/spaces/space';
import { FONT_SIZES, FONT_WEIGHTS } from '../../fundamentals/typography/typography';
import { useResponsive } from '../../hooks/useResponsive/useResponsive';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Button, IButtonProps } from '../Button/Button';
import { BUTTON_MODES, BUTTON_VARIANTS } from '../Button/buttonVariants';
import { Ellipsis } from '../Ellipsis/Ellipsis';
import { MaterialIcon, MATERIAL_ICONS } from '../Icon/icons';
import { BaseDiv } from '../../common/BaseElements';

export interface IPaginationProps extends IGutterProps {
  page?: number;
  count?: number;
  onPageChange?: (page: number) => void;
}

interface INode {
  icon?: MaterialIcon;
  pageNumber?: number;
  testId?: string;
}

const Container = styled(BaseDiv)<IGutterProps & Pick<IButtonProps, 'disabled'>>`
  display: flex;
  justify-content: center;
  ${useGutterCss()};
`;

const SIBLING_SMALL = 1;
const SIBLING_MEDIUM = 3;
const SIBLING_LARGE = 5;
const MAX_NODES_FOR_MINI_MODE = 5;

type ContainerSizes = typeof SIZES.LARGE | typeof SIZES.SMALL;

const containerSizeSiblingsMap: Record<ContainerSizes, number> = {
  [SIZES.SMALL]: SIBLING_SMALL,
  [SIZES.LARGE]: SIBLING_MEDIUM
};

const isValidPagingNumber = (num: number): boolean => Number.isInteger(num) && num > 0;

export const getNumberOfSiblingsForWidthAndPageCount = (width: number, pageCount: number): number => {
  if (pageCount <= MAX_NODES_FOR_MINI_MODE) return 0;
  const result = Object.keys(containerSizeSiblingsMap).find((cs) => width <= breakpointsWidthHelper[cs as BreakpointSize]);
  return containerSizeSiblingsMap[result as ContainerSizes] ?? SIBLING_LARGE;
};

const getSiblings = (numSiblings: number, page: number, count: number) => {
  // based on currentPage and numSiblings, create list of all siblings: e.g.
  // [5, 6, 7] when numSiblings 3 and current page is 6 or [-1, 0, 1, 2, 3] when numSiblings 5 and current page is 1 (invalid siblings are not added)
  const middleSibling = Math.ceil(numSiblings / 2);
  return [...Array.from({ length: numSiblings }).keys()]
    .filter((i) => {
      const pageIndex = page + i + middleSibling - numSiblings;
      return pageIndex > 0 && pageIndex <= count;
    })
    .map((i) => page + i + middleSibling - numSiblings);
};

export const getNodes = (page: number, count: number, width: number): readonly INode[] => {
  if (!isValidPagingNumber(page) || !isValidPagingNumber(count) || page > count || count === 1) return [];
  let nodes: readonly INode[] = [];
  const numSiblings = getNumberOfSiblingsForWidthAndPageCount(width, count);
  const addNode = (pageNumber?: number, testId?: string, icon?: MaterialIcon) =>
    (nodes = [...nodes, { testId: testId ?? `Page.${pageNumber}`, pageNumber: pageNumber, icon }]);

  if (page !== 1) addNode(page - 1, 'LeftArrow', MATERIAL_ICONS.ChevronLeft);

  if (numSiblings === 0) {
    [...Array.from({ length: count }).keys()].forEach((p) => addNode(p + 1));
  } else {
    const siblings = getSiblings(numSiblings, page, count);
    const firstSibling = siblings.at(0);
    const lastSibling = siblings.at(-1);

    if (firstSibling !== 1) addNode(1);
    if (firstSibling! > 2) addNode(undefined, 'LeftEllipsis');

    siblings.forEach((s) => addNode(s));
    if (lastSibling! < count - 1) addNode(undefined, 'RightEllipsis');
    if (lastSibling !== count) addNode(count);
  }

  if (page !== count) addNode(page + 1, 'RightArrow', MATERIAL_ICONS.ChevronRight);

  return nodes;
};

export const Pagination: React.FC<IPaginationProps> = ({ count = 1, page = 1, onPageChange, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'Pagination');
  const { width, ref } = useResponsive();
  const nodes = useMemo(() => getNodes(page, count, width), [page, count, width]);

  return (
    <>
      {nodes?.length > 0 && (
        <Container ref={ref} {...generateTestId()} {...rest}>
          {nodes.map(({ pageNumber, icon, testId }, key) => {
            const isCurrent = page === pageNumber;
            return (
              <React.Fragment key={key}>
                {pageNumber && (
                  <Button
                    startIcon={icon}
                    variant={isCurrent ? BUTTON_VARIANTS.OPAQUE_BLACK : BUTTON_VARIANTS.SECONDARY}
                    mode={isCurrent ? BUTTON_MODES.BOLD : BUTTON_MODES.MINIMAL}
                    selected={isCurrent}
                    size={SIZES.SMALL}
                    constantWidth={true}
                    onClick={() => onPageChange?.(pageNumber!)}
                    keepStyleOnDisabled={true}
                    disabled={isCurrent}
                    {...generateTestId(testId)}
                  >
                    {!icon && pageNumber}
                  </Button>
                )}

                {!pageNumber && <Ellipsis textSize={FONT_SIZES.TEXT_DEFAULT} fontWeight={FONT_WEIGHTS.MEDIUM} {...generateTestId(testId)}></Ellipsis>}
              </React.Fragment>
            );
          })}
        </Container>
      )}
    </>
  );
};
